import React from 'react';
import styled, { css } from 'styled-components';
import { AlertHeader, Button, ModalFactory, ModalFooter, ModalLayout, ModalText } from 'components';
import { theme, toRem } from 'design';
import { ICheck } from 'types';
import { centsToDollarsDisplay } from 'utils';

const ModalContent = styled(ModalLayout)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: ${toRem(265)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    padding: ${toRem(28)};
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  padding: ${toRem(16)};
  flex-direction: column;
  justify-content: center;
  gap: ${toRem(8)};
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.neutral[200]};
  width: 100%;
  margin-top: ${toRem(16)};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.white};
  width: 100%;
  max-height: ${toRem(200)};
  overflow: hidden;
  position: relative;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  background: ${theme.color.white};
  z-index: 1;
  border-bottom: 1px solid ${theme.color.neutral[200]};
  width: 100%;
`;

const TableHeaderCell = styled.th`
  padding: ${toRem(12)} ${toRem(16)};
  color: ${theme.color.neutral[500]};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(12)};
  white-space: nowrap;
  background: ${theme.color.white};
  text-align: left;
  min-width: ${toRem(120)};
`;

const TableBody = styled.tbody`
  display: block;
  max-height: ${toRem(150)};
  overflow-y: auto;
  width: 100%;
`;

const TableRow = styled.tr`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TableCell = styled.td<{ $wrap?: boolean }>`
  padding: ${toRem(12)} ${toRem(16)};
  font-size: ${toRem(12)};
  text-align: left;
  min-width: ${toRem(120)};

  ${({ $wrap }) =>
    $wrap &&
    css`
      word-break: break-word; // Fallback for older browsers and IE
      overflow-wrap: anywhere;
      white-space: normal;
    `};
`;

interface IIgnoreChecksModalProps {
  checks: ICheck[];
  isLoading?: boolean;
  onClose: () => void;
  onApply: () => void;
}

export const IgnoreChecksModal = ({ checks, onClose, onApply, isLoading }: IIgnoreChecksModalProps) => (
  <ModalFactory customModal onClose={onClose} isActive>
    <ModalContent maxWidth={600} onClose={onClose} isActive data-cy="ignore-checks-modal">
      <AlertHeader title={`Ignore check${checks.length > 1 ? 's' : ''}?`} />
      <ModalText className="modal-text">The following check{checks.length > 1 ? 's' : ''} will not be included in this payout.</ModalText>
      <DetailsWrapper>
        <Details>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Check #</TableHeaderCell>
                <TableHeaderCell>Employee</TableHeaderCell>
                <TableHeaderCell>Total</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checks.map((check: ICheck, idx: number) => (
                <TableRow key={`check-${idx}`}>
                  <TableCell>#{check.displayId}</TableCell>
                  <TableCell $wrap>{check.employeeName}</TableCell>
                  <TableCell>{centsToDollarsDisplay(check.totals.gross)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Details>
      </DetailsWrapper>
      <ModalFooter columns={2} data-cy="modal-footer">
        <Button data-cy="close-btn" label="Cancel" type="button" appearance="white" size="large" onClick={onClose} disabled={isLoading} />
        <Button data-cy="apply-btn" label="Ignore" type="button" appearance="destructive" size="large" onClick={onApply} isProcessing={isLoading} />
      </ModalFooter>
    </ModalContent>
  </ModalFactory>
);
